import React, { useEffect, useRef } from "react";
import cn from "classnames";
import Autosuggest from "react-autosuggest";
import ErrorMessage from "components/ui/ErrorMessage";
import useOuterClick from "hooks/useOuterClick";
import useDebounce from "hooks/useDebounce";
import StationsList from "components/StationsList";
import SrOnly from "components/helpers/SrOnly";
import { noop } from "lib/utils";
import { addRecentStation } from "lib/recentStations";
import { getStations, getHardCodedStations, MinSearchLength } from "services/StationsService";
import { ReactComponent as ClearFieldIcon } from "static/images/clear-field.svg";
import { ReactComponent as LoadingIcon } from "static/images/loading.svg";
import "styles/components/station-autosuggest.scss";
import useConfig from "hooks/useConfig";
import t from "lib/i18n";
import useGTM from "hooks/useGTM";
import { trackGeolocationStart, trackGeolocationError, trackGeolocationFound } from "lib/tracking";

const DEBOUNCE_SEARCH_MS = 500;

// Keep track when there's a focus click so that onClick won't hide the StationsList
let isFocusClick = false;

const getSuggestionValue = suggestion => suggestion.name;

const renderSuggestion = ({ name, crs }) => (
  <div className="otrl-jp__station-autosuggest__item">
    <span className="otrl-jp__station-autosuggest__item__name">{name}</span>
    {crs && <span className="otrl-jp__station-autosuggest__item__crs">{crs}</span>}
  </div>
);

const renderSuggestionsContainer = ({ containerProps, children, query }) => {
  const { className, ...restContainerProps } = containerProps;
  return (
    <div
      style={{
        pointerEvents: children?.props?.items.some(item => item.disabled) ? "none" : "auto"
      }}
      className={cn(className, "otrl-jp__station-autosuggest__container")}
      aria-label={t("station.results.aria")}
      {...restContainerProps}
    >
      {children}
    </div>
  );
};

const StationAutosuggest = ({
  className,
  clearErrors = noop,
  disableGeolocation = false,
  disablePopularStations = false,
  errors,
  isModal = false,
  location,
  mockStationsList,
  name,
  onSelection = noop,
  placeholder,
  setValue,
  ticketSearchType,
  ticketType,
  value,
  initialSearch = value?.name || "",
  ...props
}) => {
  const { apiUrl } = useConfig();
  const inputElement = useRef(null);
  const [searchTerm, setSearchTerm] = React.useState(initialSearch);
  const [isLoading, setLoading] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState([]);
  const [showList, setShowList] = React.useState(false);
  const [ariaStatus, setAriaStatus] = React.useState();

  const [arrowNavigation, setArrowNavigation] = React.useState(false);

  const refOuterClick = useOuterClick(e => {
    setShowList(false);
  });

  const getAutoSuggestRef = autosuggest => {
    if (autosuggest !== null) {
      inputElement.current = autosuggest.input;

      const parentElement = inputElement?.current?.parentElement;
      if (parentElement && parentElement.classList.contains('react-autosuggest__container')) {
        parentElement.setAttribute('aria-label', name === 'stationFrom' ? t("station.container.from.aria") : t("station.container.to.aria"));
      }
    }
  };

  /*
  const focusInput = () => {
    return inputElement?.current.focus?.();
  };
  */

  const handleClearField = e => {
    setValue(name, "", false);
    setSearchTerm("");
    setAriaStatus(false);
    setTimeout(() => inputElement.current?.focus()); // focus after the state updates flush so we do not trigger a fetch
  };

  const setValueAndRemember = (name, value) => {
    clearErrors(name);
    setValue(name, value, true);
    try {
      addRecentStation({ station: value, name, ticketType });
    } catch (e) {
      // This can fail incognito, cross-domain. Or due to quota issues.
      console.warn(`Could not store '${name}' in localStorage`);
    }
  };

  const getSuggestions = async (searchTerm, ticketSearchType, mockStationsList) => {
    setLoading(true);

    const stations = mockStationsList
      ? await getHardCodedStations(searchTerm, mockStationsList)
      : await getStations(searchTerm, 10, apiUrl, ticketSearchType);

    setLoading(false);
    setSuggestions(stations);

    if (searchTerm === "" && stations.length > 0) {
      setValueAndRemember(name, stations[0]);
    }

    if (searchTerm.length >= MinSearchLength) {
      setAriaStatus(true);
    }
  };

  const debouncedGetSuggestions = useDebounce(getSuggestions, DEBOUNCE_SEARCH_MS);

  useEffect(() => {
    if (value) {
      setSearchTerm(value?.name || "");
    }
  }, [value]);

  const onSuggestionSelected = (event, { suggestion }) => {
    onSelection();
    setValueAndRemember(name, suggestion); // Sets the form's value
  };

  const onClick = event => {
    if (searchTerm === "" && !isFocusClick) {
      setShowList(!showList);
    } else {
      isFocusClick = false;
    }
  };

  const onChange = (event, { newValue, method }) => {
    if (newValue === t("station.noResults")) {
      return null;
    }

    setSearchTerm(newValue); // Sets the input value

    if (method === "click") {
      onSelection(newValue);
    }

    if (newValue === "") {
      setShowList(true);
    }

    if (newValue !== "") {
      setShowList(false);
    }

    if (newValue === "" && isModal) {
      setValue(name, "", true);
      setShowList(true);
    }
  };

  const onFocus = event => {
    setShowList(true);
    isFocusClick = true;
  };

  const onBlur = event => {
    event.preventDefault();
    // setTimeout(() => setShowList(false), BLUR_TIMEOUT_MS);
  };

  const dispatchGTM = useGTM();
  const onLocationError = error => {
    dispatchGTM(trackGeolocationError());
    setLoading(false);
  };

  const onLocationFound = () => {
    dispatchGTM(trackGeolocationFound());
    setLoading(false);
  };

  const onLocationStart = () => {
    dispatchGTM(trackGeolocationStart());
    setLoading(true);
  };

  const onSuggestionsFetchRequested = ({ value }) => {
    debouncedGetSuggestions(value, ticketSearchType, mockStationsList);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
    setAriaStatus(false);
  };

  const setValueFromList = value => {
    inputElement.current.focus();
    setValueAndRemember(name, value);
    setShowList(false);
    onSelection(value);
  };

  const isError = errors && errors[name];

  const inputProps = {
    ...props,
    className: cn(className, { "otrl-jp__station-autosuggest--error": isError }),
    name,
    onBlur,
    onChange,
    onClick,
    onFocus,
    placeholder,
    value: searchTerm,
    onKeyDown: e => {
      if (e.keyCode === 13) {
        e.preventDefault();
        onSelection();
      }

      e.keyCode === 27 && setShowList(false);
      e.keyCode === 9 && setShowList(false);
      e.keyCode === 40 && setArrowNavigation(true);
    },
    onKeyPress: e => {
      e.key === "Enter" && e.preventDefault();
    }
  };

  const numStations = suggestions.filter(item => !item.disabled).length;
  const pluralise = (num, word, wordPlural = `${word}s`) => num === 1 ? word : wordPlural;

  return (
    <div
      className={cn("otrl-jp__station-input__autosuggest-wrapper", {
        "otrl-jp__station-input__autosuggest-wrapper--right": !isModal && name === "stationTo"
      })}
      ref={refOuterClick}
    >
      {ariaStatus && (
        <SrOnly role="status">
          {`${numStations} ${pluralise(numStations, "station")} found`}
        </SrOnly>
      )}
      <Autosuggest
        // alwaysRenderSuggestions /* Useful for debugging */
        getSuggestionValue={getSuggestionValue}
        highlightFirstSuggestion
        inputProps={inputProps}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        ref={getAutoSuggestRef}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={renderSuggestionsContainer}
        suggestions={suggestions}
        onSuggestionSelected={onSuggestionSelected}
      />
      {searchTerm.length > 0 && (
        <ClearFieldIcon
          role="button"
          tabIndex="0"
          aria-label="Clear input"
          className="otrl-jp__station-autosuggest__clear-field"
          onClick={handleClearField}
        />
      )}
      {isLoading && <LoadingIcon className="otrl-jp__station-autosuggest__loading" />}
      {showList && (
        <StationsList
          hideList={() => setShowList(false)}
          location={location}
          name={name}
          onLocationError={onLocationError}
          onLocationFound={onLocationFound}
          onLocationStart={onLocationStart}
          setValue={setValueFromList}
          arrowNavigation={arrowNavigation}
          disableGeolocation={disableGeolocation}
          disablePopularStations={disablePopularStations}
          ticketType={ticketType}
        />
      )}
      {!isModal && <ErrorMessage errors={errors} name={name} />}
    </div>
  );
};

export default StationAutosuggest;
